// src/pages/vendor-directory.jsx
// Agent-facing shared vendor directory — find the team's preferred plumber,
// inspector, lender, etc. Any agent can add; grouped by category; tap to
// call/email.
import { useEffect, useMemo, useState } from "react";
import {
  Phone,
  Mail,
  Plus,
  Search,
  Trash2,
  Pencil,
  X,
  Wrench,
  Send,
} from "lucide-react";
import SendVendorToClientModal from "../components/vendors/SendVendorToClientModal";
import { useStateValue } from "../context/StateProvider";
import PageHeader from "../components/PageHeader";
import { toast } from "react-toastify";
import {
  listenVendors,
  addVendor,
  updateVendor,
  removeVendor,
  addVendorNote,
  editVendorNote,
  deleteVendorNote,
  setVendorRating,
} from "../utils/vendorDirectory";
import { vendorAddedByName, vendorNoteList } from "../utils/vendorNotes";
import EntryNotesSection, {
  RatingBadge,
} from "../components/EntryNotesSection";
import { getContactsForUser } from "../utils/firebasefunctions";
import { normalizeContact } from "../utils/contactNormalize";

const STARTER_CATEGORIES = [
  "Title",
  "Inspector",
  "Lender",
  "Stager",
  "Plumber",
  "Electrician",
  "HVAC",
  "Handyman",
  "Landscaper",
  "Roofer",
  "Cleaner",
  "Contractor",
  "Photographer",
];

const BLANK = {
  company: "",
  name: "",
  category: "",
  phone: "",
  email: "",
  notes: "",
};

const inputCls =
  "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1E293B]";

export default function VendorDirectory() {
  const [{ user, users }] = useStateValue();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  // The vendor whose detail panel is open. Notes live in there, not on the card.
  const [detailVendor, setDetailVendor] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  // Always read the live row so a note added in the panel appears immediately.
  const detail = detailVendor
    ? vendors.find((x) => x.id === detailVendor.id) || detailVendor
    : null;

  // Contacts are only loaded when the add/edit modal is first opened — there
  // can be thousands, and the directory itself doesn't need them.
  const [contacts, setContacts] = useState([]);
  const [contactQuery, setContactQuery] = useState("");

  useEffect(() => {
    if (!modalOpen || contacts.length > 0 || !user?.id) return;
    getContactsForUser(user)
      .then((all) =>
        setContacts((all || []).map(normalizeContact).filter(Boolean)),
      )
      .catch((err) => console.error("contact lookup failed:", err));
  }, [modalOpen, user?.id, contacts.length, user]);

  const contactMatches = useMemo(() => {
    const q = contactQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    const digits = q.replace(/\D/g, "");
    return contacts
      .filter((c) => {
        if ((c.name || "").toLowerCase().includes(q)) return true;
        if (digits.length >= 3) {
          return (c.primaryPhone || "").replace(/\D/g, "").includes(digits);
        }
        return false;
      })
      .slice(0, 6);
  }, [contacts, contactQuery]);

  const fillFromContact = (c) => {
    setForm((f) => ({
      ...f,
      name: c.name || f.name,
      phone: c.primaryPhone || f.phone,
      email: c.primaryEmail || f.email,
    }));
    setContactQuery("");
  };

  useEffect(() => {
    const unsub = listenVendors((rows) => setVendors(rows));
    return () => unsub && unsub();
  }, []);

  const categories = useMemo(
    () =>
      Array.from(new Set(vendors.map((v) => v.category).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [vendors],
  );

  const categoryOptions = useMemo(
    () =>
      Array.from(new Set([...STARTER_CATEGORIES, ...categories])).sort((a, b) =>
        a.localeCompare(b),
      ),
    [categories],
  );

  const grouped = useMemo(() => {
    const t = search.toLowerCase().trim();
    const filtered = vendors.filter((v) => {
      if (activeCat !== "All" && v.category !== activeCat) return false;
      if (!t) return true;
      return [v.company, v.name, v.category, v.phone, v.email].some((f) =>
        (f || "").toLowerCase().includes(t),
      );
    });
    const g = {};
    filtered.forEach((v) => {
      const c = v.category || "Other";
      (g[c] = g[c] || []).push(v);
    });
    return Object.keys(g)
      .sort((a, b) => a.localeCompare(b))
      .map((c) => ({
        category: c,
        items: g[c].sort((a, b) =>
          (a.company || a.name || "").localeCompare(b.company || b.name || ""),
        ),
      }));
  }, [vendors, search, activeCat]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...BLANK, category: activeCat !== "All" ? activeCat : "" });
    setModalOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      company: v.company || "",
      name: v.name || "",
      category: v.category || "",
      phone: v.phone || "",
      email: v.email || "",
      notes: v.notes || "",
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.company.trim() && !form.name.trim()) {
      toast.error("Add a company or a contact name");
      return;
    }
    if (!form.category.trim()) {
      toast.error("Pick a category (e.g. Plumber)");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, category: form.category.trim() };
      if (editing) await updateVendor(editing.id, payload);
      else await addVendor(payload, user?.id);
      setModalOpen(false);
      toast.success(editing ? "Vendor updated" : "Vendor added");
    } catch (err) {
      console.error("save vendor failed:", err);
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    try {
      await removeVendor(v.id);
      toast.success("Vendor removed");
    } catch (err) {
      toast.error("Could not remove.");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <PageHeader title="Vendor Directory" />

      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {/* Search + add */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendors"
              className={`${inputCls} pl-9`}
            />
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg bg-[#1E293B] hover:bg-[#0f172a] whitespace-nowrap"
          >
            <Plus size={16} /> Add vendor
          </button>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["All", ...categories].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActiveCat(c)}
              className={`text-sm px-3 py-1 rounded-full border transition-colors ${
                activeCat === c
                  ? "bg-[#1E293B] text-white border-[#1E293B]"
                  : "border-slate-300 text-slate-600 hover:bg-slate-100"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {grouped.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-16">
            No vendors yet — add your first one.
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.category} className="mb-6">
              <h3 className="text-sm font-medium text-slate-500 mb-2">
                {group.category}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.items.map((v) => (
                  <div
                    key={v.id}
                    onClick={() => setDetailVendor(v)}
                    className="group bg-white border border-slate-200 rounded-xl p-3.5 cursor-pointer hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="size-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <Wrench size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">
                          {v.company || v.name || "Vendor"}
                        </div>
                        {v.company && v.name && (
                          <div className="text-xs text-slate-500 truncate">
                            {v.name}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(v);
                          }}
                          className="text-slate-400 hover:text-slate-700"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(v);
                          }}
                          className="text-slate-400 hover:text-red-600"
                          title="Remove"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                    {(vendorAddedByName(v, users) || v.ratings) && (
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-[11px] text-slate-400">
                          {vendorAddedByName(v, users)
                            ? `Added by ${vendorAddedByName(v, users)}`
                            : ""}
                        </p>
                        <RatingBadge ratings={v.ratings} />
                      </div>
                    )}
                    {vendorNoteList(v).length > 0 && (
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">
                        {vendorNoteList(v)[0].text}
                      </p>
                    )}
                    <div className="space-y-1 mb-3 text-xs">
                      {v.phone && (
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <Phone
                            size={12}
                            className="text-slate-400 flex-shrink-0"
                          />
                          <span className="truncate">{v.phone}</span>
                        </div>
                      )}
                      {v.email && (
                        <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
                          <Mail
                            size={12}
                            className="text-slate-400 flex-shrink-0"
                          />
                          <a
                            href={`mailto:${v.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="truncate hover:text-slate-900 hover:underline"
                          >
                            {v.email}
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={v.phone ? `tel:${v.phone}` : undefined}
                        onClick={(e) => e.stopPropagation()}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm py-1.5 rounded-lg border ${
                          v.phone
                            ? "border-slate-200 text-slate-700 hover:bg-slate-50"
                            : "border-slate-100 text-slate-300 pointer-events-none"
                        }`}
                      >
                        <Phone size={14} /> Call
                      </a>
                      <a
                        href={v.email ? `mailto:${v.email}` : undefined}
                        onClick={(e) => e.stopPropagation()}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm py-1.5 rounded-lg border ${
                          v.email
                            ? "border-slate-200 text-slate-700 hover:bg-slate-50"
                            : "border-slate-100 text-slate-300 pointer-events-none"
                        }`}
                      >
                        <Mail size={14} /> Email
                      </a>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSendTarget(v);
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                      >
                        <Send size={14} /> Send to Client
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add / edit modal */}
      {detail && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setDetailVendor(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900 truncate">
                  {detail.company || detail.name || "Vendor"}
                </h3>
                <p className="text-xs text-slate-500">
                  {detail.category}
                  {detail.company && detail.name ? ` · ${detail.name}` : ""}
                </p>
                {vendorAddedByName(detail, users) && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Added by {vendorAddedByName(detail, users)}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setDetailVendor(null)}
                className="text-slate-400 hover:text-slate-700 flex-shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <Phone size={14} className="text-slate-400 flex-shrink-0" />
                  {detail.phone ? (
                    <span>{detail.phone}</span>
                  ) : (
                    <span className="text-slate-300">No phone number</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-slate-700 min-w-0">
                  <Mail size={14} className="text-slate-400 flex-shrink-0" />
                  {detail.email ? (
                    <a
                      href={`mailto:${detail.email}`}
                      className="truncate hover:underline"
                    >
                      {detail.email}
                    </a>
                  ) : (
                    <span className="text-slate-300">No email address</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href={detail.phone ? `tel:${detail.phone}` : undefined}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm py-2 rounded-lg border ${
                    detail.phone
                      ? "border-slate-200 text-slate-700 hover:bg-slate-50"
                      : "border-slate-100 text-slate-300 pointer-events-none"
                  }`}
                >
                  <Phone size={14} /> Call
                </a>
                <a
                  href={detail.email ? `mailto:${detail.email}` : undefined}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm py-2 rounded-lg border ${
                    detail.email
                      ? "border-slate-200 text-slate-700 hover:bg-slate-50"
                      : "border-slate-100 text-slate-300 pointer-events-none"
                  }`}
                >
                  <Mail size={14} /> Email
                </a>
              </div>

              <EntryNotesSection
                entries={detail.noteEntries}
                legacyNotes={detail.notes}
                ratings={detail.ratings}
                user={user}
                onAddNote={(note) => addVendorNote(detail.id, note)}
                onEditNote={(ref, text) =>
                  editVendorNote(detail.id, ref, text)
                }
                onDeleteNote={(ref) => deleteVendorNote(detail.id, ref)}
                onRate={(stars) => setVendorRating(detail.id, user?.id, stars)}
              />
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                {editing ? "Edit vendor" : "Add vendor"}
              </h3>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Pull from your contacts
                </label>
                <input
                  className={inputCls}
                  placeholder="Search contacts by name or phone"
                  value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                />
                {contactMatches.length > 0 && (
                  <div className="mt-1 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-44 overflow-y-auto">
                    {contactMatches.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => fillFromContact(c)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50"
                      >
                        <span className="block text-sm text-slate-800">
                          {c.name}
                        </span>
                        <span className="block text-xs text-slate-400">
                          {c.primaryPhone || c.primaryEmail || ""}
                          {c._teamOwnerName && <span> · {c._teamOwnerName}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Category
                </label>
                <input
                  className={inputCls}
                  list="vendor-categories"
                  placeholder="e.g. Plumber"
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                />
                <datalist id="vendor-categories">
                  {categoryOptions.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Company
                  </label>
                  <input
                    className={inputCls}
                    placeholder="Rapid Rooter Plumbing"
                    value={form.company}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, company: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Contact name
                  </label>
                  <input
                    className={inputCls}
                    placeholder="Mike Alvarez"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Phone
                  </label>
                  <input
                    className={inputCls}
                    placeholder="(555) 123-4567"
                    value={form.phone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, phone: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Email
                  </label>
                  <input
                    className={inputCls}
                    placeholder="mike@rapidrooter.com"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Notes
                </label>
                <textarea
                  rows={2}
                  className={`${inputCls} resize-y`}
                  placeholder="e.g. Fast, fair pricing. Ask for the agent rate."
                  value={form.notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, notes: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold rounded-md bg-[#1E293B] hover:bg-[#0f172a] text-white disabled:opacity-50"
              >
                {saving ? "Saving…" : editing ? "Save" : "Add vendor"}
              </button>
            </div>
          </div>
        </div>
      )}

      <SendVendorToClientModal
        open={!!sendTarget}
        vendor={sendTarget}
        onClose={() => setSendTarget(null)}
      />
    </div>
  );
}
